import { DATA_KEYS, readJSON, writeJSON, removeKey } from "@/storage/local";
import { getEmail } from "@/storage/session";
import { starterDiet, DEFAULT_MEALS, WEEKDAYS } from "@/data/defaultDiet";

// Dieta semanal del usuario: siete días por comida, texto libre.
//
// Se guarda entera en una sola clave, que la capa de sincronización recoge sola
// por estar en DATA_KEYS. Cabe de sobra bajo el límite de Firestore: son 35
// líneas de texto.
//
// Una cuenta nueva arranca con la dieta vacía. La única que arranca escrita es
// la del dueño, que trae la suya de papel; el resto la escribe cada uno.

const EMPTY = "";

// Rellena lo que falte con la forma esperada. Una dieta guardada por una
// versión anterior puede no tener todas las comidas, y una fila corta dejaría
// celdas en `undefined` que la interfaz pintaría como "undefined".
function normalize(diet) {
  const meals = Array.isArray(diet?.meals) && diet.meals.length > 0 ? diet.meals : DEFAULT_MEALS;
  const plan = {};

  meals.forEach((meal) => {
    const row = Array.isArray(diet?.plan?.[meal.id]) ? diet.plan[meal.id] : [];
    plan[meal.id] = WEEKDAYS.map((_, i) => (typeof row[i] === "string" ? row[i] : EMPTY));
  });

  return {
    meals,
    plan,
    // Una nota vacía es una respuesta válida: no se rellena con la de nadie.
    note: typeof diet?.note === "string" ? diet.note : EMPTY,
  };
}

export function loadDiet() {
  const stored = readJSON(DATA_KEYS.DIET, null);
  if (stored) return normalize(stored);
  // Sin nada guardado se entrega la dieta de partida, pero no se escribe: si el
  // usuario nunca entra aquí, su cuenta no arrastra una dieta que no ha pedido.
  return normalize(starterDiet(getEmail()));
}

// ¿Hay algo escrito? El informe la omite si no, y así no saca una sección con
// treinta y cinco guiones.
export function hasDietContent(diet) {
  return diet.meals.some((meal) => (diet.plan[meal.id] || []).some((text) => text.trim()));
}

// Restaurar deja la dieta en blanco en casi todas las cuentas, así que el
// diálogo que lo pregunta necesita saberlo para no prometer otra cosa.
export function starterIsEmpty() {
  return !hasDietContent(normalize(starterDiet(getEmail())));
}

export function saveDiet(diet) {
  const next = normalize(diet);
  writeJSON(DATA_KEYS.DIET, next);
  return next;
}

// Cambia una celda concreta. Devuelve la dieta completa ya guardada.
export function setMealEntry(diet, mealId, dayIndex, text) {
  const plan = { ...diet.plan, [mealId]: [...(diet.plan[mealId] || [])] };
  plan[mealId][dayIndex] = text;
  return saveDiet({ ...diet, plan });
}

// El desayuno es el mismo los siete días, y la merienda casi: copiar a mano la
// misma línea siete veces es justo lo que hace abandonar una pantalla así.
export function setMealEveryDay(diet, mealId, text) {
  return saveDiet({ ...diet, plan: { ...diet.plan, [mealId]: WEEKDAYS.map(() => text) } });
}

export function renameMeal(diet, mealId, name) {
  return saveDiet({
    ...diet,
    meals: diet.meals.map((meal) => (meal.id === mealId ? { ...meal, name } : meal)),
  });
}

export function addMeal(diet, name) {
  const meal = { id: `meal_${Date.now()}`, name };
  return saveDiet({
    ...diet,
    meals: [...diet.meals, meal],
    plan: { ...diet.plan, [meal.id]: WEEKDAYS.map(() => EMPTY) },
  });
}

export function deleteMeal(diet, mealId) {
  const plan = { ...diet.plan };
  delete plan[mealId];
  return saveDiet({ ...diet, meals: diet.meals.filter((meal) => meal.id !== mealId), plan });
}

export function setNote(diet, note) {
  return saveDiet({ ...diet, note });
}

// Devuelve la dieta al punto de partida de esta cuenta, que para casi todo el
// mundo significa dejarla en blanco.
export function resetDiet() {
  return saveDiet(starterDiet(getEmail()));
}

export function clearDiet() {
  removeKey(DATA_KEYS.DIET);
}

// Índice de hoy en la semana, con el lunes primero: getDay() da 0 al domingo.
export function todayIndex() {
  return (new Date().getDay() + 6) % 7;
}
